import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  Slider,
  InputNumber,
  Row,
  Col,
  Card,
  Space,
  Typography,
  Tooltip,
  message,
} from "antd";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useLlmProviders } from "../../../hooks/useLlmProviders";
import ClientAiFields from "./ClientAiFields";

const { Text } = Typography;

const ConfigurationTab = ({ agent, onSaved }) => {
  const [form] = Form.useForm();
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const [models, setModels] = useState([]);
  const { options: providerOptions } = useLlmProviders();
  const [saving, setSaving] = useState(false);

  const loadModels = useCallback(async (provider) => {
    try {
      const { data } = await mutateRef.current("fetchLlmModelsConfig", {
        method: "get",
        endpoint: "/v1/llm-models",
        params: provider ? { provider } : {},
      });
      setModels(data?.data || []);
    } catch {
      setModels([]);
    }
  }, []);

  useEffect(() => {
    if (!agent) return;
    form.setFieldsValue({
      name: agent.name,
      description: agent.description,
      provider: agent.provider,
      model: agent.model,
      systemPrompt: agent.systemPrompt,
      temperature: agent.temperature ?? 0.7,
      maxTokens: agent.maxTokens,
      topP: agent.topP ?? 1,
      externalAgentId: agent.externalAgentId,
      useClientAi: !!agent.useClientAi,
      clientAiApiKey: undefined,
    });
    loadModels(agent.provider);
  }, [agent, form, loadModels]);

  const onProviderChange = (p) => {
    form.setFieldValue("model", undefined);
    loadModels(p);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await mutate("updateAgent", {
        method: "put",
        endpoint: `/v1/agents/${agent.agentId}`,
        // A blank key keeps the stored one; switching Client AI off deletes it.
        payload: { ...values, clientAiApiKey: values.clientAiApiKey || undefined },
        alert: { success: "Agent saved — a new version was created." },
      });
      onSaved?.();
    } catch (err) {
      if (err?.errorFields) return; // validation error
      message.error("Failed to save agent");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical">
      <Row gutter={[24, 0]}>
        <Col xs={24} lg={16}>
          <Card
            size="small"
            title="Training"
            extra={
              <Tooltip title="The system prompt is the core training instruction. Describe behaviour, tone and constraints. It becomes part of every conversation.">
                <InfoCircleOutlined />
              </Tooltip>
            }
          >
            <Form.Item
              name="systemPrompt"
              label="System prompt"
              tooltip="This is the primary 'training' of the agent — like giving an employee a job description."
            >
              <Input.TextArea
                rows={14}
                placeholder={`You are a helpful assistant specialised in ...\n\nAlways respond in ...\nNever ...`}
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
              />
            </Form.Item>
          </Card>

          <Card size="small" title="Identity" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: "Name is required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="externalAgentId"
                  label={
                    <Space>
                      External Assistant ID
                      <Tooltip title="Optional. If set, the platform hands off to an OpenAI Assistant or Claude agent configured remotely.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Input placeholder="asst_..." />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card size="small" title="Model">
            <Form.Item
              name="provider"
              label="Provider"
              rules={[{ required: true }]}
            >
              <Select
                onChange={onProviderChange}
                options={providerOptions}
                placeholder="Choose a provider"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="model"
              label="LLM model"
              tooltip="Choose from the models installed in this platform. Managed centrally — contact admin to add new ones."
            >
              <Select
                placeholder="Select a model"
                options={models.map((m) => ({
                  value: m.modelId,
                  label: m.displayName,
                  key: m.modelId,
                }))}
                showSearch
                optionFilterProp="label"
                allowClear
                popupRender={(menu) => (
                  <>
                    {menu}
                    {models.length === 0 && (
                      <div style={{ padding: 8 }}>
                        <Text type="secondary">
                          No models registered for this provider.
                        </Text>
                      </div>
                    )}
                  </>
                )}
              />
            </Form.Item>

            <ClientAiFields
              isGlobal={agent?.isGlobal}
              keyHint={agent?.hasClientAiKey ? agent?.clientAiApiKeyHint || "…" : null}
            />
          </Card>

          <Card size="small" title="Generation" style={{ marginTop: 16 }}>
            <Form.Item
              name="temperature"
              label={
                <Space>
                  Temperature
                  <Tooltip title="0 = deterministic, 1 = creative. Leave default for general tasks.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
            >
              <Slider min={0} max={2} step={0.05} />
            </Form.Item>
            <Form.Item
              name="topP"
              label={
                <Space>
                  Top P
                  <Tooltip title="Nucleus sampling — restricts token choices to the top-P probability mass.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
            >
              <Slider min={0} max={1} step={0.05} />
            </Form.Item>
            <Form.Item
              name="maxTokens"
              label="Max output tokens"
              tooltip="Upper bound per response. Leave empty to use the server default (16000). For long structured outputs (e.g. invoices with many line items) set explicitly to 16000–32000 to avoid truncation."
            >
              <InputNumber min={1} max={200000} style={{ width: "100%" }} />
            </Form.Item>
          </Card>

          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button
              variant="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default ConfigurationTab;
